/**
 * Интерпретатор языка предусловий `when` (ТЗ §3.2).
 *
 * Язык замкнутый и декларативный: предусловие сериализуемо, статически проверяемо
 * схемой и не может выполнить произвольный код из манифеста.
 */

import type { Comparison, Predicate, PredicateContextKey, PredicateValue } from '../manifests/schema';

export interface OnboardingContext {
  mailboxCount: number;
  pinnedCount: number;
  deletedCount: number;
  trafficUsedPercent: number;
  atMailboxLimit: boolean;
  forwardingReady: boolean;
  plan: string;
  breakpoint: 'mobile' | 'tablet' | 'desktop';
  route: string;
}

function isCombinator(node: Predicate, key: 'all' | 'any' | 'not'): boolean {
  return Object.prototype.hasOwnProperty.call(node, key);
}

function compare(actual: PredicateValue, comparison: Comparison): boolean {
  if (comparison.eq !== undefined && actual !== comparison.eq) return false;
  if (comparison.ne !== undefined && actual === comparison.ne) return false;
  if (comparison.in !== undefined && !comparison.in.includes(actual)) return false;

  const numeric = comparison.gt ?? comparison.gte ?? comparison.lt ?? comparison.lte;
  if (numeric !== undefined) {
    // Числовые операторы к строке или булеву неприменимы: считаем предусловие
    // невыполненным, а не приводим типы молча.
    if (typeof actual !== 'number') return false;
    if (comparison.gt !== undefined && !(actual > comparison.gt)) return false;
    if (comparison.gte !== undefined && !(actual >= comparison.gte)) return false;
    if (comparison.lt !== undefined && !(actual < comparison.lt)) return false;
    if (comparison.lte !== undefined && !(actual <= comparison.lte)) return false;
  }

  return true;
}

/** Пустое предусловие (`null` / `undefined`) означает «шаг применим всегда». */
export function evaluatePredicate(
  predicate: Predicate | null | undefined,
  context: OnboardingContext
): boolean {
  if (!predicate) return true;

  if (isCombinator(predicate, 'all')) {
    const { all } = predicate as { all: Predicate[] };
    return all.every((child) => evaluatePredicate(child, context));
  }
  if (isCombinator(predicate, 'any')) {
    const { any } = predicate as { any: Predicate[] };
    return any.some((child) => evaluatePredicate(child, context));
  }
  if (isCombinator(predicate, 'not')) {
    const { not } = predicate as { not: Predicate };
    return !evaluatePredicate(not, context);
  }

  const entries = Object.entries(predicate) as [PredicateContextKey, Comparison][];
  return entries.every(([key, comparison]) => compare(context[key], comparison));
}
